import { useEffect } from 'react';
import { useProjectStore } from '../store/useProjectStore';
import { useSSEStore } from '../store/useSSEStore';
import { useExtractionStore } from '../store/useExtractionStore';
import { loadDiagramReview } from '../store/useReviewStore';
import { useCollectionStore } from '../store/useCollectionStore';
import { usePromptStore } from '../store/usePromptStore';
import { loadAllResults } from '../init';
import * as api from '../api/endpoints';
import type { AgentEvent, ApprovalRequest, PromptEntry } from '../types';

/**
 * Subscribe to the project's SSE stream, reconnecting when `projectId`
 * changes (client-side project switches, no page reload).
 *
 * The connection is scoped per-project via `?project_id=` (EventSource can't
 * send headers). On first mount it adopts the early connection opened in
 * index.html — but only if that connection was opened for the same project —
 * to keep any events buffered before React mounted. Otherwise it opens a
 * fresh stream.
 */
export function useSSE(projectId: string | null) {
  useEffect(() => {
    if (!projectId) return;

    // Adopt the pre-React connection only when it matches this project;
    // otherwise close it and open a fresh, correctly-scoped stream.
    let es: EventSource;
    let adoptedEarly = false;
    if (window.__earlySSE && window.__earlySSEProjectId === projectId) {
      es = window.__earlySSE;
      adoptedEarly = true;
      window.__earlySSE = undefined;
      window.__earlySSEProjectId = undefined;
    } else {
      if (window.__earlySSE) {
        window.__earlySSE.close();
        window.__earlySSE = undefined;
        window.__earlySSEProjectId = undefined;
      }
      es = new EventSource(`/api/events?project_id=${encodeURIComponent(projectId)}`);
    }

    const sseStore = useSSEStore.getState();
    // Clear the previous project's stream state before draining this
    // connection's buffered events, so the drain is not wiped afterward.
    sseStore.resetForNewRun();
    sseStore.setConnected(true);

    // Handler functions
    function handleAgentEvent(raw: string) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const r: any = JSON.parse(raw);

        // Map backend field names (event/tool/ts/text/result) to frontend format
        const evt: AgentEvent = {
          type: r.type || r.event,
          tool_name: r.tool_name || r.tool,
          timestamp: r.timestamp || (r.ts ? new Date(r.ts * 1000).toISOString() : undefined),
          turn: r.turn,
          args: r.args,
          skill: r.skill,
          status: r.status,
          summary: r.summary,
          elapsed_ms: r.elapsed_ms,
          metrics: r.metrics,
          final_observations: r.final_observations,
          plan: r.plan,
        };

        // Map event-specific data fields
        if (evt.type === 'thinking') {
          evt.data = r.data ?? r.text;
        } else if (evt.type === 'fallback') {
          evt.data = r.data ?? r.reason;
        } else if (evt.type === 'warning') {
          evt.data = r.data ?? r.message;
        } else if (evt.type === 'waiting') {
          evt.data = r.data ?? `Turn ${r.turn || '?'} — waiting (${r.completed || 0} completed)`;
        } else if (evt.type === 'plan_set') {
          evt.data = r.data ?? `Plan set with ${r.step_count || '?'} steps`;
        } else if (evt.type === 'compaction') {
          evt.data = r.data ?? `Context compacted: ${r.old_length} → ${r.new_length} messages`;
        } else if (evt.type === 'finalize') {
          evt.summary = r.summary;
        } else {
          evt.data = r.data;
        }

        // For tool_result, extract from nested result object
        if (evt.type === 'tool_result' && r.result && typeof r.result === 'object') {
          evt.status = evt.status || r.result.status;
          evt.summary = evt.summary || r.result.summary;
          evt.elapsed_ms = evt.elapsed_ms || r.result.elapsed_ms;
          evt.metrics = evt.metrics || r.result.metrics;
        }

        const sseState = useSSEStore.getState();

        if (evt.type === 'tool_call' && evt.tool_name) {
          const skillName = evt.tool_name.replace(/^run_/, '');
          sseState.setSkillStatus(skillName, {
            name: skillName,
            status: 'running',
            startTime: Date.now(),
          });
        }

        if (evt.type === 'tool_result' && evt.tool_name) {
          const skillName = evt.tool_name.replace(/^run_/, '');
          const status = evt.status === 'error' ? 'failed' as const : 'success' as const;
          sseState.setSkillStatus(skillName, {
            name: skillName,
            status,
            elapsed: evt.elapsed_ms,
            summary: evt.summary,
            metrics: evt.metrics as Record<string, unknown> | undefined,
          });
          sseState.clearSkillProgress(skillName);
        }

        if (evt.type === 'turn_start' && evt.turn) {
          sseState.setAgentTurnCount(evt.turn);
        }

        // Handle skill_progress events written to agent_events.jsonl
        if (r.type === 'skill_progress') {
          const skill = r.skill as string;
          if (skill && typeof r.processed === 'number' && typeof r.total === 'number') {
            sseState.setSkillProgress(skill, {
              processed: r.processed,
              total: r.total,
              detail: r.detail,
            });
            // Auto-run emits aggregate progress but no tool_call event — mark
            // the skill running so skillStatuses (sidebar/header + the
            // extraction lock) reflects it.
            if (skill === 'diagram_extraction') {
              sseState.setSkillStatus(skill, { name: skill, status: 'running' });
            }
          }
          return; // Don't add to agent feed
        }

        sseState.addAgentEvent(evt);
      } catch { /* ignore parse errors */ }
    }

    function handleSkillComplete(raw: string) {
      try {
        const data = JSON.parse(raw) as { skill: string };
        reloadSkillResult(data.skill);

        const projStore = useProjectStore.getState();

        // Signal key phase transitions via tab attention (non-disruptive)
        if (data.skill === 'research_report') {
          projStore.setTabAttention('research', true);
          projStore.setToast('Research complete — review findings');
        } else if (data.skill === 'diagram_extraction') {
          // Auto-extraction finished (success or partial) — mark the skill done
          // so the lock clears + sidebar/header update. reloadSkillResult above
          // already refreshed the data in place (no page reload).
          useSSEStore.getState().setSkillStatus('diagram_extraction', {
            name: 'diagram_extraction',
            status: 'success',
          });
          useSSEStore.getState().clearSkillProgress('diagram_extraction');
          projStore.setTabAttention('extraction', true);
          projStore.setToast('Extraction complete');
        } else if (data.skill === 'floor_plan_annotation') {
          projStore.setTabAttention('annotation', true);
          projStore.setToast('Floor plan annotation complete');
        } else if (data.skill === 'review_diagrams_grouped' || data.skill === 'review_diagrams_matched') {
          // The agent's diagram→LV matching wrote a review file — refresh the
          // Review tab in place (preserve expanded rows), advancing the wizard.
          loadDiagramReview({ preserveView: true }).catch(() => {});
          const phase = data.skill === 'review_diagrams_matched' ? 'review_match' : 'review_group';
          useSSEStore.getState().clearSkillProgress(phase);
          if (data.skill === 'review_diagrams_matched') {
            projStore.setTabAttention('review', true);
            projStore.setToast('Diagram LV matching complete');
          }
        } else if (data.skill === 'incremental_status') {
          // An incremental "analyze new files" run finished — refresh the
          // header button (new-files) and the re-run-LV-matching banner.
          projStore.markIncrementalAnalysisRefreshed();
          projStore.setToast('New files analyzed');
        }
      } catch { /* ignore */ }
    }

    function handleLlmPrompt(raw: string) {
      try {
        const entry: PromptEntry = JSON.parse(raw);
        usePromptStore.getState().addPrompt(entry);
      } catch { /* ignore */ }
    }

    function handleApprovalRequest(raw: string) {
      try {
        const req: ApprovalRequest = JSON.parse(raw);
        const skillName = req.skill_name || req.skill || '';

        // Research-phase skills auto-approve (read-only analysis)
        const AUTO_APPROVE_SKILLS = new Set([
          'pre_scan', 'triage', 'separation', 'boq_extraction', 'research_report',
        ]);
        const toolSkill = (req.tool_name || '').replace(/^run_/, '');
        const isAutoApprove = AUTO_APPROVE_SKILLS.has(skillName) || AUTO_APPROVE_SKILLS.has(toolSkill);

        if (isAutoApprove) {
          api.approve(req.request_id);
          useSSEStore.getState().addAgentEvent({
            type: 'approval_request',
            data: { ...req, auto_approved: true },
            timestamp: new Date().toISOString(),
            tool_name: skillName,
          });
          return;
        }

        // Everything else requires user approval: gates, result reviews,
        // extraction-phase skills (diagram_extraction, floor_plan_annotation)
        const current = useSSEStore.getState().pendingApproval;
        if (current?.request_id === req.request_id) return;
        useSSEStore.getState().setPendingApproval(req);
        useSSEStore.getState().addAgentEvent({
          type: 'approval_request',
          data: req,
          timestamp: new Date().toISOString(),
          tool_name: skillName,
        });

        // Signal attention on the appropriate tab
        const projStore = useProjectStore.getState();
        const isGate = skillName.endsWith('_gate') || req.type === 'result_review';
        if (skillName.includes('research')) {
          projStore.setTabAttention('research', true);
          projStore.setToast('Research gate — review required');
        } else if (skillName.includes('extraction') || skillName.includes('diagram') || req.type === 'result_review') {
          projStore.setTabAttention(isGate ? 'extraction' : 'pipeline', true);
          projStore.setToast(isGate ? 'Extraction gate — review required' : `Approve ${skillName.replace(/_/g, ' ')}?`);
        } else if (skillName === 'floor_plan_annotation') {
          projStore.setTabAttention('pipeline', true);
          projStore.setToast('Start floor plan counting — approve in Pipeline tab');
        } else {
          projStore.setTabAttention('pipeline', true);
          projStore.setToast(isGate ? 'Gate approval required' : `Approve ${skillName.replace(/_/g, ' ')}?`);
        }
      } catch { /* ignore */ }
    }

    function handlePageReviewRequest(raw: string) {
      try {
        const req: ApprovalRequest = JSON.parse(raw);
        const extStore = useExtractionStore.getState();
        const projStore = useProjectStore.getState();

        // Signal attention on extraction tab for page review
        if (projStore.activeTab !== 'extraction') {
          projStore.setTabAttention('extraction', true);
        }

        // Auto-approve if enabled (skips all user review)
        if (extStore.autoApprovePages) {
          api.approvePageReview(req.request_id);
          const key = `${req.file}:${req.page}`;
          extStore.setPageStatus(key, 'extracting');
          return;
        }

        // Queue review — user will browse pages and batch-approve per file
        extStore.addPendingReview(req);
        const key = `${req.file}:${req.page}`;
        extStore.setPageStatus(key, 'reviewing');
      } catch { /* ignore */ }
    }

    function handleFloorPlanReviewRequest(raw: string) {
      try {
        const req: ApprovalRequest = JSON.parse(raw);
        const projStore = useProjectStore.getState();

        // Add to floor plan review queue (separate from extraction reviews)
        projStore.addFpPendingReview(req);

        // Set attention on annotation tab (floor plans live there)
        if (projStore.activeTab !== 'annotation') {
          projStore.setTabAttention('annotation', true);
        }

        // Auto-activate first review
        if (!projStore.fpCurrentReview) {
          const next = projStore.shiftFpPendingReview();
          if (next) {
            projStore.setFpCurrentReview(next);
          }
        }
      } catch { /* ignore */ }
    }

    function handleSkillProgress(raw: string) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const evt: any = JSON.parse(raw);

        // Track structured progress (processed/total) for any skill
        if (evt.skill && typeof evt.processed === 'number' && typeof evt.total === 'number') {
          useSSEStore.getState().setSkillProgress(evt.skill, {
            processed: evt.processed,
            total: evt.total,
            detail: evt.detail,
          });
          // Auto-run emits aggregate progress but no tool_call event — mark
          // the skill running so skillStatuses reflects it (lock + sidebar).
          if (evt.skill === 'diagram_extraction') {
            useSSEStore.getState().setSkillStatus('diagram_extraction', {
              name: 'diagram_extraction',
              status: 'running',
            });
          }
        }

        if (evt.message === 'page_complete') {
          const key = `${evt.file}:${evt.page}`;
          const extStore = useExtractionStore.getState();
          extStore.setPageStatus(key, 'done');
          useCollectionStore.getState().touchDiagram(evt.file as string);
          extStore.setPageResult(key, {
            page: evt.page,
            file: evt.file,
            zone_id: evt.zone_id,
            new_components: evt.new_components || [],
            cables_and_labor: evt.cables_and_labor || [],
            auxiliary_contacts_count: evt.auxiliary_contacts_count,
            ignored_items: evt.ignored_items,
            reasoning_summary: evt.reasoning_summary,
          });

          // Clear currentReview if it matches this completed page
          const cr = extStore.currentReview;
          if (cr && cr.file === evt.file && cr.page === evt.page) {
            extStore.setCurrentReview(null);
            // Advance to next pending review
            const next = extStore.shiftPendingReview();
            if (next) extStore.setCurrentReview(next);
          }
        }
      } catch { /* ignore */ }
    }

    // Attach listeners to the early SSE
    function handleProjectChanged() {
      // Project switched or re-run — clear all accumulated state
      useSSEStore.getState().resetForNewRun();
      loadAllResults().catch(() => {});
    }

    const handlers: Record<string, (raw: string) => void> = {
      agent_event: handleAgentEvent,
      skill_complete: handleSkillComplete,
      llm_prompt: handleLlmPrompt,
      approval_request: handleApprovalRequest,
      result_review: handleApprovalRequest,
      page_review_request: handlePageReviewRequest,
      floor_plan_review_request: handleFloorPlanReviewRequest,
      skill_progress: handleSkillProgress,
      project_changed: handleProjectChanged,
    };

    for (const [eventName, handler] of Object.entries(handlers)) {
      es.addEventListener(eventName, ((e: MessageEvent) => {
        handler(e.data);
      }) as EventListener);
    }

    // Replay events buffered before React mounted — only when we adopted
    // the early connection (a fresh stream has no pre-buffered events).
    if (adoptedEarly) {
      const buffer = window.__earlySSEBuffer;
      if (buffer) {
        window.__earlySSEBuffer = undefined; // Stop buffering
        for (const evt of buffer) {
          const handler = handlers[evt.type];
          if (handler) handler(evt.data);
        }
      }
    }

    // Poll pending approvals as fallback (covers all approval types)
    const pollInterval = setInterval(async () => {
      const pending = await api.getPendingApprovals();
      if (pending) {
        for (const req of pending) {
          // Skip already-acted-upon requests (belt-and-suspenders with backend filter)
          if (req.action && req.action !== 'pending') continue;
          if (req.type === 'floor_plan_review') {
            handleFloorPlanReviewRequest(JSON.stringify(req));
          } else if (req.type === 'page_review') {
            handlePageReviewRequest(JSON.stringify(req));
          } else {
            // Skill approval or result review
            handleApprovalRequest(JSON.stringify(req));
          }
        }
      }
    }, 3000);

    // Reload all results when tab becomes visible again (covers SSE drops during long absence)
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        loadAllResults().catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      es.close();
      useSSEStore.getState().setConnected(false);
      clearInterval(pollInterval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [projectId]);
}

async function reloadSkillResult(skill: string) {
  const store = useProjectStore.getState();
  const loaders: Record<string, () => Promise<void>> = {
    triage: async () => { const r = await api.getTriage(); if (r) store.setTriage(r); },
    separation: async () => { const r = await api.getSeparation(); if (r) store.setSeparation(r); },
    research_report: async () => { const r = await api.getResearchReport(); if (r) store.setResearchReport(r); },
    diagram_extraction: async () => { const r = await api.getExtraction(); if (r) store.setExtraction(r); },
    boq_extraction: async () => { const r = await api.getBoqExtraction(); if (r) store.setBoqExtraction(r); },
    floor_plan_annotation: async () => { const r = await api.getFloorPlanAnnotation(); if (r) store.setFloorPlanAnnotation(r); },
    pre_scan: async () => { const r = await api.getPreScan(); if (r) store.setPreScan(r); },
  };
  const loader = loaders[skill];
  if (loader) await loader();

  // Also reload renders
  const renders = await api.getRenders();
  if (renders) store.setRenders(renders);
}
